import {
  Button,
  Card,
  CardBody,
  Stat,
  StatGroup,
  StatHelpText,
  StatLabel,
  StatNumber,
  Text,
} from "@chakra-ui/react";
import AddCapitalEvent from "features/capital-activity/AddCapitalEventModal";
import { getCapitalActivityEventStateBadge } from "features/capital-activity/CapitalActivityUtils";
import { useGetCapitalEventMenuItems } from "features/capital-activity/event/CapitalEventMenu";
import { useCapitalActivitySummaryRetrieveSuspense } from "gen/endpoints/capital-activity/capital-activity";
import { CapitalActivityAPIData } from "gen/model/capitalActivityAPIData";
import { GenericTableData } from "gen/model/genericTableData";
import { GenericTableRow } from "gen/model/genericTableRow";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import { TFunction } from "i18next";
import { useCallback, useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Restricted } from "shared/components/access/Restricted";
import {
  CardSection,
  CardSectionBody,
  CardSectionGroup,
} from "shared/components/card/CardSection";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { StatProgress } from "shared/components/stat/StatProgress";
import { StatTooltip } from "shared/components/stat/StatTooltip";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import {
  GetLinkInfoForRowFn,
  RenderRowActionsFn,
} from "shared/components/table/data/DataTableTypes";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";
import { getPercentage, getRoundedPercentage } from "shared/utils/MathUtils";
import { formatMoney } from "shared/utils/MoneyUtils";
import { useBoolean } from "usehooks-ts";

// TODO: Move this to the backend
const parseCapitalActivityToRow = (
  capitalActivity: CapitalActivityAPIData,
  t: TFunction<["common", "capitalActivity"]>,
): GenericTableRow => ({
  id: capitalActivity.id,
  granularity_level: "fund_family",
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  metadata: capitalActivity,
  cells: {
    type: {
      badge: capitalActivity.has_draft_reallocation
        ? {
            type: "text",
            color: "warning",
            text: t("capitalActivity:reallocation.title"),
          }
        : null,
      value: {
        type: "text",
        text: capitalActivity.event_type,
      },
    },
    state: {
      badge: getCapitalActivityEventStateBadge(capitalActivity.state, t),
    },
    date: {
      value: {
        type: "date",
        date: capitalActivity.effective_date,
      },
    },
    notes: {
      value: {
        type: "text",
        text: capitalActivity.notes,
      },
    },
    capital_call_amount: {
      value: {
        type: "money",
        amount: capitalActivity.capital_call_amount,
        currency: "USD",
      },
    },
    distribution_amount: {
      value: {
        type: "money",
        amount: capitalActivity.distribution_amount,
        currency: "USD",
      },
    },
    equalization: {
      value: {
        type: "boolean",
        value: capitalActivity.equalization,
      },
    },
    net_amount: {
      value: {
        type: "money",
        amount: capitalActivity.net_amount,
        currency: "USD",
      },
    },
  },
});

export const CapitalActivityPage: React.FC = () => {
  const {
    value: isAddModalOpen,
    setTrue: openAddModal,
    setFalse: closeAddModal,
  } = useBoolean();

  const { fundFamilyId } = useRequiredStringParams();
  const { t } = useTranslation(["common", "capitalActivity", "capitalCall"]);
  const navigate = useNavigate();

  const { data, refetch } =
    useCapitalActivitySummaryRetrieveSuspense(fundFamilyId);

  const getCapitalEventMenuItems = useGetCapitalEventMenuItems(fundFamilyId);

  const getLinkInfoForRow = useCallback<GetLinkInfoForRowFn>(
    ({ row }) =>
      row.id
        ? {
            type: "generic",
            to: rootRoute({})
              .fundFamilies({})
              .fundFamily({ fundFamilyId })
              .capitalActivity({})
              .capitalActivityEvent({
                fundFamilyEventId: row.id,
              }).$,
          }
        : undefined,
    [fundFamilyId],
  );

  const renderRowActions = useCallback<RenderRowActionsFn>(
    ({ row }) => {
      const metadata = row.data?.metadata as unknown as CapitalActivityAPIData;
      const menuItems = getCapitalEventMenuItems(metadata.id, metadata.state);
      if (menuItems.length) {
        return {
          type: "menu",
          actions: menuItems,
        };
      }
    },
    [getCapitalEventMenuItems],
  );

  const capitalActivityData = useMemo(
    (): GenericTableData => ({
      columns: [
        {
          id: "type",
          title: t("common:type"),
          is_header: true,
          filter_type: "set",
        },
        { id: "state", title: t("common:state") },
        { id: "date", title: t("common:date") },
        {
          id: "capital_call_amount",
          title: t("capitalActivity:totalCapitalCalled"),
        },
        {
          id: "distribution_amount",
          title: t("capitalActivity:totalDistribution"),
        },
        { id: "net_amount", title: t("common:netAmount") },
        { id: "equalization", title: t("capitalActivity:equalization") },
        { id: "notes", title: t("common:notesOptional") },
      ],
      rows: data.capital_activity_data.map<GenericTableRow>((element) =>
        parseCapitalActivityToRow(element, t),
      ),
    }),
    [data, t],
  );

  return (
    <PageContent isFullHeight={true}>
      <PageHeader
        title={t("common:capitalActivity")}
        actions={
          <Restricted permission={PermissionTypeEnum.write}>
            <Button colorScheme="secondary" size="sm" onClick={openAddModal}>
              {t("capitalActivity:runCapitalEvent")}
            </Button>
          </Restricted>
        }
      />
      <PageBody>
        <Card variant="stat" mb="24px">
          <CardBody px="32px">
            <CardSectionGroup>
              <CardSection>
                <StatGroup>
                  <StatTooltip
                    label={t("capitalActivity:statsTooltip", {
                      percentage: getRoundedPercentage(
                        data.called_capital,
                        data.total_amount_committed,
                      ),
                    })}
                  >
                    <Stat size="xs">
                      <StatLabel>
                        {t("capitalActivity:calledCapital")}
                      </StatLabel>
                      <StatNumber>
                        {formatMoney(data.called_capital)}
                      </StatNumber>
                      <StatProgress
                        value={getPercentage(
                          data.called_capital,
                          data.total_amount_committed,
                        )}
                      />
                    </Stat>
                  </StatTooltip>
                  <StatTooltip
                    label={t("capitalActivity:statsTooltip", {
                      percentage: getRoundedPercentage(
                        data.unfunded_capital,
                        data.total_amount_committed,
                      ),
                    })}
                  >
                    <Stat size="xs">
                      <StatLabel>
                        {t("capitalActivity:unfundedCapital")}
                      </StatLabel>
                      <StatNumber>
                        {formatMoney(data.unfunded_capital)}
                      </StatNumber>
                      <StatProgress
                        value={getPercentage(
                          data.unfunded_capital,
                          data.total_amount_committed,
                        )}
                      />
                    </Stat>
                  </StatTooltip>
                  <StatTooltip
                    label={t("capitalActivity:statsTooltip", {
                      percentage: getRoundedPercentage(
                        data.distributed_capital,
                        data.total_amount_committed,
                      ),
                    })}
                  >
                    <Stat size="xs">
                      <StatLabel>
                        {t("capitalActivity:distributedCapital")}
                      </StatLabel>
                      <StatNumber>
                        {formatMoney(data.distributed_capital)}
                      </StatNumber>
                      <StatProgress
                        value={getPercentage(
                          data.distributed_capital,
                          data.total_amount_committed,
                        )}
                      />
                      <StatHelpText>
                        <Trans
                          ns="capitalActivity"
                          i18nKey="recallableDistributions"
                          values={{
                            amount: formatMoney(data.recallable_capital),
                          }}
                        >
                          Recallable Distributions:
                          <Text as="span" color="gray.600">
                            $2,000,000
                          </Text>
                        </Trans>
                      </StatHelpText>
                    </Stat>
                  </StatTooltip>
                </StatGroup>
              </CardSection>
              <CardSection>
                <CardSectionBody>
                  <StatGroup>
                    <Stat size="xs">
                      <StatLabel>
                        {t("capitalActivity:totalAmountCommitted")}
                      </StatLabel>
                      <StatNumber>
                        {formatMoney(data.total_amount_committed)}
                      </StatNumber>
                    </Stat>
                  </StatGroup>
                </CardSectionBody>
              </CardSection>
            </CardSectionGroup>
          </CardBody>
        </Card>
        <TableBox>
          <TableBoxHeader>
            <TableBoxTitle>{t("capitalActivity:capitalEvents")}</TableBoxTitle>
            <DataTableBoxActions />
          </TableBoxHeader>
          <TableBoxBody>
            <DataTable
              data={capitalActivityData}
              getLinkInfoForRow={getLinkInfoForRow}
              renderRowActions={renderRowActions}
              emptyStateProps={{
                title: t("capitalActivity:empty.title"),
                description: t("capitalActivity:empty.description"),
              }}
            />
          </TableBoxBody>
        </TableBox>
        {isAddModalOpen && (
          <AddCapitalEvent
            fund_family_id={fundFamilyId}
            onCloseComplete={closeAddModal}
            onSuccess={async (fundFamilyEventId) => {
              await refetch();
              navigate(
                rootRoute({})
                  .fundFamilies({})
                  .fundFamily({ fundFamilyId })
                  .capitalActivity({})
                  .capitalActivityEvent({ fundFamilyEventId }).$,
              );
            }}
          />
        )}
      </PageBody>
    </PageContent>
  );
};
